import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/contexts/auth-context';
import { DashboardLayout } from '@/components/layout/dashboard-layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/lib/supabase';
import { Calendar, Clock, BookOpen, Users, DollarSign, Video, Radio, FileQuestion, Layers, ArrowLeft, Check, Award, BarChart3, Globe, GraduationCap, UserCheck } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import { useToast } from '@/hooks/use-toast';
import { Link } from 'react-router-dom';
import { CourseChat } from '@/components/course/course-chat';

interface Course {
  id: string;
  title: string;
  description: string;
  price: number;
  thumbnail_url: string | null;
  course_type: string;
  level: string;
  language: string;
  category: string;
  start_date: string | null;
  end_date: string | null;
  is_published: boolean;
  category_id: string;
  teacher_id: string;
  max_students: number | null;
  created_at: string;
  profiles: {
    full_name: string;
    email: string;
  };
  categories: {
    name: string;
  };
}

const courseTypeIcons = {
  video: Video,
  live: Radio,
  quiz: FileQuestion,
  mixed: Layers,
};

const courseTypeLabels = {
  video: 'Video Corso',
  live: 'Live Corso',
  quiz: 'Quiz',
  mixed: 'Misto',
};

export default function CourseDetailPage() {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const params = useParams();
  const { toast } = useToast();
  const [course, setCourse] = useState<Course | null>(null);
  const [loading, setLoading] = useState(true);
  const [isEnrolled, setIsEnrolled] = useState(false);
  const [enrolling, setEnrolling] = useState(false);
  const [enrollmentCount, setEnrollmentCount] = useState(0);
  const [useRewardPoints, setUseRewardPoints] = useState(false);
  const [pointsToUse, setPointsToUse] = useState(0);

  const courseId = params.id as string;
  const studentPoints = profile?.reward_points || 0;

  useEffect(() => {
    if (profile) {
      fetchCourse();
      checkEnrollment();
      fetchEnrollmentCount();
    }
  }, [profile, courseId]);

  const fetchCourse = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('courses')
      .select(`
        *,
        profiles:teacher_id(full_name, email),
        categories:category_id(name)
      `)
      .eq('id', courseId)
      .maybeSingle();

    if (error) {
      console.error('Error fetching course:', error);
      toast({
        title: 'Errore',
        description: 'Impossibile caricare il corso',
        variant: 'destructive',
      });
    } else if (data) {
      setCourse(data);
    }
    setLoading(false);
  };

  const checkEnrollment = async () => {
    const { data } = await supabase
      .from('enrollments')
      .select('id')
      .eq('course_id', courseId)
      .eq('student_id', profile?.id)
      .maybeSingle();

    setIsEnrolled(!!data);
  };

  const fetchEnrollmentCount = async () => {
    const { count } = await supabase
      .from('enrollments')
      .select('*', { count: 'exact', head: true })
      .eq('course_id', courseId);

    setEnrollmentCount(count || 0);
  };

  const calculateFinalPrice = () => {
    if (!course) return 0;
    const discount = useRewardPoints ? pointsToUse : 0;
    return Math.max(0, course.price - discount);
  };

  const handleUsePointsChange = (checked: boolean) => {
    setUseRewardPoints(checked);
    if (checked && course) {
      const maxPointsUsable = Math.min(studentPoints, course.price);
      setPointsToUse(maxPointsUsable);
    } else {
      setPointsToUse(0);
    }
  };

  const handleEnroll = async () => {
    if (!course) return;

    const finalPrice = calculateFinalPrice();

    if (finalPrice === 0) {
      setEnrolling(true);

      if (useRewardPoints && pointsToUse > 0) {
        const { error: pointsError } = await supabase
          .from('reward_points_transactions')
          .insert({
            student_id: profile?.id,
            points: -pointsToUse,
            reason: `Acquisto corso: ${course.title}`,
            type: 'spent',
            course_id: courseId,
          });

        if (pointsError) {
          toast({
            title: 'Errore',
            description: 'Impossibile utilizzare i punti premio',
            variant: 'destructive',
          });
          setEnrolling(false);
          return;
        }
      }

      const { error } = await supabase
        .from('enrollments')
        .insert({
          course_id: courseId,
          student_id: profile?.id,
          payment_status: 'completed',
        });

      if (error) {
        toast({
          title: 'Errore',
          description: 'Impossibile completare l\'iscrizione',
          variant: 'destructive',
        });
      } else {
        toast({
          title: 'Iscrizione completata',
          description: useRewardPoints ? `Ti sei iscritto usando ${pointsToUse} punti premio!` : 'Ti sei iscritto con successo al corso',
        });
        setIsEnrolled(true);
        fetchEnrollmentCount();
      }
      setEnrolling(false);
    } else {
      setEnrolling(true);
      try {
        const response = await fetch('/api/paypal/create-order', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            courseId,
            studentId: profile?.id,
            amount: finalPrice.toString(),
            pointsUsed: useRewardPoints ? pointsToUse : 0,
          }),
        });

        const data = await response.json();

        if (data.approvalUrl) {
          window.location.href = data.approvalUrl;
        } else {
          throw new Error('No approval URL');
        }
      } catch (error) {
        toast({
          title: 'Errore',
          description: 'Impossibile avviare il pagamento',
          variant: 'destructive',
        });
        setEnrolling(false);
      }
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="space-y-6">
          <Skeleton className="h-12 w-48" />
          <Skeleton className="h-96" />
        </div>
      </DashboardLayout>
    );
  }

  if (!course) {
    return (
      <DashboardLayout>
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <BookOpen className="w-12 h-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Corso non trovato
            </h3>
            <p className="text-gray-600 text-center mb-4">
              Il corso che stai cercando non esiste o non è più disponibile
            </p>
            <Link to="/dashboard/calendar">
              <Button>Torna al calendario</Button>
            </Link>
          </CardContent>
        </Card>
      </DashboardLayout>
    );
  }

  const Icon = courseTypeIcons[course.course_type as keyof typeof courseTypeIcons] || BookOpen;
  const isFull = course.max_students !== null && enrollmentCount >= course.max_students;
  const finalPrice = calculateFinalPrice();
  const maxPointsUsable = course ? Math.min(studentPoints, course.price) : 0;
  const isTeacher = profile?.id === course.teacher_id;
  const canAccessChat = isEnrolled || isTeacher;

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <Link to="/dashboard/calendar">
          <Button variant="ghost" size="sm">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Torna al calendario
          </Button>
        </Link>

        <div className="grid gap-6 lg:grid-cols-3">
          <div className="lg:col-span-2 space-y-6">
            <Card className="overflow-hidden">
              <div className="aspect-video bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center relative">
                {course.thumbnail_url ? (
                  <img
                    src={course.thumbnail_url}
                    alt={course.title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <Icon className="w-24 h-24 text-white opacity-50" />
                )}
                <div className="absolute top-4 left-4">
                  <Badge variant="secondary" className="text-base">
                    {courseTypeLabels[course.course_type as keyof typeof courseTypeLabels]}
                  </Badge>
                </div>
                {isEnrolled && (
                  <div className="absolute top-4 right-4">
                    <Badge className="bg-green-600 text-base">
                      <Check className="w-4 h-4 mr-1" />
                      Iscritto
                    </Badge>
                  </div>
                )}
              </div>

              <CardHeader>
                <CardTitle className="text-2xl md:text-3xl">{course.title}</CardTitle>
                <CardDescription className="text-base">
                  {course.description}
                </CardDescription>
              </CardHeader>

              <CardContent className="space-y-6">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="flex items-center gap-3 p-3 bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl hover:shadow-md transition-shadow">
                    <div className="p-3 bg-white rounded-lg shadow-sm">
                      <Users className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-xs text-blue-700 font-medium">Docente</p>
                      <p className="font-bold text-gray-900">{course.profiles?.full_name}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 p-3 bg-gradient-to-br from-violet-50 to-violet-100 rounded-xl hover:shadow-md transition-shadow">
                    <div className="p-3 bg-white rounded-lg shadow-sm">
                      <BookOpen className="w-5 h-5 text-violet-600" />
                    </div>
                    <div>
                      <p className="text-xs text-violet-700 font-medium">Categoria</p>
                      <p className="font-bold text-gray-900">{course.categories?.name || course.category}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 p-3 bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-xl hover:shadow-md transition-shadow">
                    <div className="p-3 bg-white rounded-lg shadow-sm">
                      <BarChart3 className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div>
                      <p className="text-xs text-emerald-700 font-medium">Livello</p>
                      <p className="font-bold text-gray-900">{course.level || 'Non specificato'}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 p-3 bg-gradient-to-br from-cyan-50 to-cyan-100 rounded-xl hover:shadow-md transition-shadow">
                    <div className="p-3 bg-white rounded-lg shadow-sm">
                      <Globe className="w-5 h-5 text-cyan-600" />
                    </div>
                    <div>
                      <p className="text-xs text-cyan-700 font-medium">Lingua</p>
                      <p className="font-bold text-gray-900">{course.language || 'Italiano'}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 p-3 bg-gradient-to-br from-amber-50 to-amber-100 rounded-xl hover:shadow-md transition-shadow">
                    <div className="p-3 bg-white rounded-lg shadow-sm">
                      <UserCheck className="w-5 h-5 text-amber-600" />
                    </div>
                    <div>
                      <p className="text-xs text-amber-700 font-medium">Studenti iscritti</p>
                      <p className="font-bold text-gray-900">
                        {enrollmentCount} {course.max_students ? `/ ${course.max_students}` : ''}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 p-3 bg-gradient-to-br from-rose-50 to-rose-100 rounded-xl hover:shadow-md transition-shadow">
                    <div className="p-3 bg-white rounded-lg shadow-sm">
                      <GraduationCap className="w-5 h-5 text-rose-600" />
                    </div>
                    <div>
                      <p className="text-xs text-rose-700 font-medium">Tipologia</p>
                      <p className="font-bold text-gray-900">{courseTypeLabels[course.course_type as keyof typeof courseTypeLabels]}</p>
                    </div>
                  </div>

                  {course.start_date && (
                    <div className="flex items-center gap-3 p-3 bg-gradient-to-br from-green-50 to-green-100 rounded-xl hover:shadow-md transition-shadow">
                      <div className="p-3 bg-white rounded-lg shadow-sm">
                        <Calendar className="w-5 h-5 text-green-600" />
                      </div>
                      <div>
                        <p className="text-xs text-green-700 font-medium">Data inizio</p>
                        <p className="font-bold text-gray-900">
                          {format(new Date(course.start_date), 'dd MMM yyyy', { locale: it })}
                        </p>
                      </div>
                    </div>
                  )}

                  {course.end_date && (
                    <div className="flex items-center gap-3 p-3 bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl hover:shadow-md transition-shadow">
                      <div className="p-3 bg-white rounded-lg shadow-sm">
                        <Clock className="w-5 h-5 text-orange-600" />
                      </div>
                      <div>
                        <p className="text-xs text-orange-700 font-medium">Data fine</p>
                        <p className="font-bold text-gray-900">
                          {format(new Date(course.end_date), 'dd MMM yyyy', { locale: it })}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {canAccessChat && (
              <CourseChat courseId={courseId} isTeacher={isTeacher} />
            )}
          </div>

          <div className="lg:col-span-1">
            <Card className="sticky top-6">
              <CardHeader>
                <CardTitle>Iscrizione</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {profile?.role === 'studente' && studentPoints > 0 && (
                  <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Award className="w-5 h-5 text-yellow-600" />
                        <span className="font-semibold text-yellow-800">I tuoi punti</span>
                      </div>
                      <span className="text-xl font-bold text-yellow-600">{studentPoints}</span>
                    </div>
                    <p className="text-xs text-yellow-700">1 punto = 1 euro di sconto</p>
                  </div>
                )}

                <div className="space-y-3">
                  <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg">
                    <span className="text-gray-600">Prezzo originale</span>
                    <div className="flex items-center text-xl font-bold text-gray-600">
                      <DollarSign className="w-5 h-5" />
                      {course.price === 0 ? 'Gratuito' : course.price.toFixed(2)}
                    </div>
                  </div>

                  {!isEnrolled && profile?.role === 'studente' && course.price > 0 && studentPoints > 0 && (
                    <>
                      <div className="flex items-center space-x-2 p-3 bg-gray-50 rounded-lg">
                        <Checkbox
                          id="use-points"
                          checked={useRewardPoints}
                          onCheckedChange={handleUsePointsChange}
                        />
                        <label
                          htmlFor="use-points"
                          className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                        >
                          Usa {maxPointsUsable} punti premio ({maxPointsUsable.toFixed(2)} di sconto)
                        </label>
                      </div>

                      {useRewardPoints && (
                        <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg border border-green-200">
                          <span className="text-green-700 font-semibold">Sconto punti</span>
                          <div className="flex items-center text-lg font-bold text-green-600">
                            - <DollarSign className="w-4 h-4" />
                            {pointsToUse.toFixed(2)}
                          </div>
                        </div>
                      )}

                      <div className="flex items-center justify-between p-4 bg-blue-600 text-white rounded-lg">
                        <span className="font-semibold">Totale da pagare</span>
                        <div className="flex items-center text-2xl font-bold">
                          <DollarSign className="w-6 h-6" />
                          {finalPrice === 0 ? 'Gratuito' : finalPrice.toFixed(2)}
                        </div>
                      </div>
                    </>
                  )}
                </div>

                {isEnrolled ? (
                  <div className="space-y-3">
                    <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex items-center gap-2 text-green-700">
                        <Check className="w-5 h-5" />
                        <span className="font-semibold">Sei già iscritto a questo corso</span>
                      </div>
                    </div>
                    <Link to="/dashboard/my-learning">
                      <Button className="w-full">
                        Vai ai miei corsi
                      </Button>
                    </Link>
                  </div>
                ) : (
                  <>
                    {isFull ? (
                      <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                        <p className="text-red-700 font-semibold text-center">
                          Corso al completo
                        </p>
                      </div>
                    ) : (
                      <Button
                        onClick={handleEnroll}
                        disabled={enrolling}
                        className="w-full"
                        size="lg"
                      >
                        {enrolling ? 'Elaborazione...' : finalPrice === 0 ? 'Iscriviti gratis' : 'Procedi al pagamento'}
                      </Button>
                    )}

                    <div className="text-xs text-gray-500 text-center">
                      {finalPrice > 0 && 'Sarai reindirizzato a PayPal per completare il pagamento'}
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
